import { DeleteOutlined, ExportOutlined, ImportOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select, Typography } from "antd";
import { STEP_INPUT_POLICIES } from "components/CC/config/stepInputPolicies";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { MODIFY_MODIFICATION_OPTIONS, MODIFY_OPERATOR_OPTIONS } from "components/CC/Nodes/ModifyFieldNode/const";
import {
    modifyFieldFormSchema,
    OUTPUT_DISABLED_MODIFICATIONS,
    PATTERN_ENABLED_MODIFICATIONS,
    REGEX_OPERATORS,
    VALUE_DISABLED_OPERATORS,
    VALUE2_ENABLED_OPERATORS,
} from "components/CC/Nodes/ModifyFieldNode/Forms/formSchema";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import {
    insertCategoryInOrder,
    PickerCategoryType,
} from "components/CC/UI/ParameterPicker/buildParameterPickerCategories";
import { ParameterCategory } from "components/CC/UI/ParameterPicker/ParameterPicker";
import { createRowsCategory, CustomFieldData } from "components/CC/UI/ParameterPicker/parameterUtils";
import { useBuildParameterPickerCategories } from "components/CC/UI/ParameterPicker/useBuildParameterPickerCategories";
import { TagTextInput } from "components/CC/UI/TagInput/TagTextInput";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { FilterType, ModifyType } from "types/enums";
import { z } from "zod";

export type ModifyFieldFormData = z.infer<typeof modifyFieldFormSchema>;

interface FormProps {
    onChange?: (data: ModifyFieldFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formId: string;
    /** Form fields from API */
    formFields?: App.Data.FormFieldData[];
    /** Custom fields from API (Integration or FormStructure) */
    customFields?: CustomFieldData[];
}

export const ModifyFieldForm = ({ onChange, onRegisterValidator, formId, formFields, customFields }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<ModifyFieldFormData>
            key={selectedNode?.id}
            id={formId}
            schema={modifyFieldFormSchema}
            defaultValues={selectedNode?.formData as ModifyFieldFormData}
        >
            <FormContent
                onChange={onChange}
                onRegisterValidator={onRegisterValidator}
                formFields={formFields}
                customFields={customFields}
            />
        </GeneralFormProvider>
    );
};

interface FormContentProps {
    onChange?: (data: ModifyFieldFormData) => void;
    onRegisterValidator?: (fn: () => Promise<boolean>) => void;
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

const FormContent = ({ onChange, onRegisterValidator, formFields, customFields }: FormContentProps) => {
    const { control, trigger } = useGeneralForm<ModifyFieldFormData>();

    useEffect(() => {
        onRegisterValidator?.(() => trigger());
    }, [onRegisterValidator, trigger]);
    const { t } = usePaldockTranslation("components.connection-creator");
    const { fields: outerFields, append: appendOuter, remove: removeOuter } = useFieldArray({ control, name: "data" });

    return (
        <Fragment>
            <FormValueSync<ModifyFieldFormData> onChange={onChange} />
            {/* Header section */}
            <div className="bg-white rounded-lg p-4 m-3">
                <div className="flex justify-between items-center mb-4">
                    <Typography.Title level={4} className="!mb-0">
                        {t("nodes.modify.forms.settings")}
                    </Typography.Title>
                    {/* Active toggle — hidden until BE is_enabled is wired up
                    <Controller
                        control={control}
                        name="active"
                        render={({ field }) => (
                            <div className="flex items-center gap-2">
                                <Switch checked={field.value} onChange={field.onChange} />
                                <Typography.Text type="secondary">{t("nodes.modify.forms.active")}</Typography.Text>
                            </div>
                        )}
                    /> */}
                </div>

                <div className="flex gap-4">
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="title"
                        label={t("nodes.modify.forms.title")}
                    >
                        <Input placeholder={t("nodes.modify.forms.title_placeholder")} />
                    </FormItem>
                    <FormItem
                        className="flex-1 !mb-0"
                        control={control}
                        name="subtitle"
                        label={t("nodes.modify.forms.subtitle")}
                    >
                        <Input placeholder={t("nodes.modify.forms.subtitle_placeholder")} />
                    </FormItem>
                </div>
            </div>

            {/* Field sections */}
            {outerFields.map((field, outerIndex) => (
                <OuterFieldItem
                    key={field.id}
                    outerIndex={outerIndex}
                    removeOuter={removeOuter}
                    formFields={formFields}
                    customFields={customFields}
                />
            ))}
            <div className="bg-white flex justify-center items-center rounded-lg p-3 m-3 h-[120px]">
                <Button
                    icon={<PlusOutlined />}
                    type="dashed"
                    onClick={() =>
                        appendOuter({
                            fieldName: "",
                            fields: [
                                {
                                    source: "",
                                    operator: "",
                                    value: "",
                                    value2: "",
                                    modification: "",
                                    pattern: "",
                                    output: "",
                                },
                            ],
                        })
                    }
                >
                    {t("nodes.modify.forms.add_section")}
                </Button>
            </div>
        </Fragment>
    );
};

interface OuterFieldItemProps {
    outerIndex: number;
    removeOuter: (index: number) => void;
    formFields?: App.Data.FormFieldData[];
    customFields?: CustomFieldData[];
}

const OuterFieldItem = ({ outerIndex, removeOuter, formFields, customFields }: OuterFieldItemProps) => {
    const { control } = useGeneralForm<ModifyFieldFormData>();
    const { selectedNode } = useSelectedNode();
    const { t } = usePaldockTranslation("components.connection-creator");
    const buildCategories = useBuildParameterPickerCategories({
        t,
        formFields,
        customFields,
        currentNodeId: selectedNode?.id,
    });

    const {
        fields: innerFields,
        append: appendInner,
        remove: removeInner,
    } = useFieldArray({
        control,
        name: `data.${outerIndex}.fields`,
    });

    const totalRows = innerFields.length;

    // Watch all fields in this section to determine column visibility
    const allFields = useWatch({
        control,
        name: `data.${outerIndex}.fields`,
    });

    // Check if any row needs Value2 column (between operators)
    const showValue2Column = useMemo(() => {
        if (!allFields) return false;
        return allFields.some((field) => VALUE2_ENABLED_OPERATORS.includes(field?.operator as FilterType));
    }, [allFields]);

    // Check if any row needs Pattern column (regex_replace modification)
    const showPatternColumn = useMemo(() => {
        if (!allFields) return false;
        return allFields.some((field) => PATTERN_ENABLED_MODIFICATIONS.includes(field?.modification as ModifyType));
    }, [allFields]);

    // Calculate column spans based on which columns are visible
    // value and value2 have same width when both shown
    const getColSpans = () => {
        const hasValue2 = showValue2Column;
        const hasPattern = showPatternColumn;

        if (hasValue2 && hasPattern) {
            return { source: 4, operator: 3, value: 3, value2: 3, modification: 3, pattern: 4, output: 4 };
        } else if (hasValue2) {
            return { source: 6, operator: 3, value: 3, value2: 3, modification: 3, pattern: 0, output: 6 };
        } else if (hasPattern) {
            return { source: 5, operator: 3, value: 5, value2: 0, modification: 3, pattern: 3, output: 5 };
        } else {
            return { source: 6, operator: 3, value: 6, value2: 0, modification: 3, pattern: 0, output: 6 };
        }
    };

    const targetCategories = useMemo(
        () =>
            buildCategories({
                allowedCategories: [...STEP_INPUT_POLICIES.modify.target.ui.allowedCategories],
            }),
        [buildCategories]
    );

    const valueCategories = useMemo(
        () =>
            buildCategories({
                allowedCategories: [...STEP_INPUT_POLICIES.modify.filterParams.ui.allowedCategories],
            }),
        [buildCategories]
    );

    const outputCategories = useMemo(
        () =>
            buildCategories({
                allowedCategories: [...STEP_INPUT_POLICIES.modify.changeParams.ui.allowedCategories],
            }),
        [buildCategories]
    );

    // "rows" depends on innerIndex and is cheap to build (a flat list), but the rest of the
    // categories (form fields, previous steps, etc.) are identical for every row in this section
    // and expensive to build (previous-steps graph walk). Build the shared part once per section
    // instead of once per row — building it per row made opening a section with many rows (e.g.
    // ~90) effectively O(rows) re-runs of that expensive computation and could hang the drawer.
    const sourceAllowedCategories = STEP_INPUT_POLICIES.modify.source.ui.allowedCategories as PickerCategoryType[];

    const sourceBaseCategories = useMemo(
        () =>
            buildCategories({
                allowedCategories: sourceAllowedCategories.filter((category) => category !== "rows"),
            }),
        [buildCategories, sourceAllowedCategories]
    );

    const innerSourceCategoriesMap = useMemo(
        () =>
            innerFields.map((_, innerIndex) => {
                const rowsCategory = createRowsCategory(
                    {
                        totalRows,
                        currentRowIndex: innerIndex,
                        onlyPreviousRows: true,
                        rowLabel: (rowNumber) => t("nodes.modify.forms.source_options.row", { number: rowNumber }),
                    },
                    t
                );
                return insertCategoryInOrder(sourceBaseCategories, rowsCategory, "rows", sourceAllowedCategories);
            }),
        [sourceBaseCategories, sourceAllowedCategories, innerFields.length, totalRows, t]
    );
    const colSpans = getColSpans();

    return (
        <div className="bg-white rounded-lg p-3 m-3">
            {/* Section header with title, Import/Export buttons, and delete */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={5} className="!mb-0">
                    {`${t("nodes.modify.forms.modify")} ${outerIndex + 1}`}
                </Typography.Title>
                <div className="flex gap-2 items-center">
                    <Button type="default" size="middle" icon={<ImportOutlined />} disabled>
                        {t("nodes.modify.forms.import")}
                    </Button>
                    <Button type="default" size="middle" icon={<ExportOutlined />} disabled>
                        {t("nodes.modify.forms.export")}
                    </Button>
                    <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeOuter(outerIndex)}
                    />
                </div>
            </div>

            {/* Field name */}
            <FormItem
                className="mb-4"
                control={control}
                name={`data.${outerIndex}.fieldName`}
                label={t("nodes.modify.forms.field")}
            >
                <TagTextInput
                    placeholder={t("nodes.modify.forms.field_placeholder")}
                    categories={targetCategories}
                    mode={STEP_INPUT_POLICIES.modify.target.ui.mode}
                    maxTags={1}
                />
            </FormItem>
            {innerFields.length > 0 && (
                <div className="flex gap-2 mb-2">
                    <Row className="flex-1" gutter={8}>
                        <Col span={colSpans.source}>
                            <Typography.Text type="secondary">{t("nodes.modify.forms.source")}</Typography.Text>
                        </Col>
                        <Col span={colSpans.operator}>
                            <Typography.Text type="secondary">{t("nodes.modify.forms.operator")}</Typography.Text>
                        </Col>
                        <Col span={colSpans.value}>
                            <Typography.Text type="secondary">{t("nodes.modify.forms.value")}</Typography.Text>
                        </Col>
                        {showValue2Column && (
                            <Col span={colSpans.value2}>
                                <Typography.Text type="secondary">{t("nodes.modify.forms.value2")}</Typography.Text>
                            </Col>
                        )}
                        <Col span={colSpans.modification}>
                            <Typography.Text type="secondary">{t("nodes.modify.forms.modification")}</Typography.Text>
                        </Col>
                        {showPatternColumn && (
                            <Col span={colSpans.pattern}>
                                <Typography.Text type="secondary">{t("nodes.modify.forms.pattern")}</Typography.Text>
                            </Col>
                        )}
                        <Col span={colSpans.output}>
                            <Typography.Text type="secondary">{t("nodes.modify.forms.output")}</Typography.Text>
                        </Col>
                    </Row>
                    <div className="w-[32px]" />
                </div>
            )}
            {innerFields.map((field, innerIndex) => (
                <InnerFieldsRenderer
                    key={field.id}
                    outerIndex={outerIndex}
                    innerIndex={innerIndex}
                    removeInner={removeInner}
                    sourceCategories={innerSourceCategoriesMap[innerIndex]}
                    valueCategories={valueCategories}
                    outputCategories={outputCategories}
                    showValue2Column={showValue2Column}
                    showPatternColumn={showPatternColumn}
                    colSpans={colSpans}
                />
            ))}
            <Button
                type="dashed"
                icon={<PlusOutlined />}
                className="w-full"
                onClick={() =>
                    appendInner({
                        source: "",
                        operator: "",
                        value: "",
                        value2: "",
                        modification: "",
                        pattern: "",
                        output: "",
                    })
                }
            >
                {t("nodes.modify.forms.add_row")}
            </Button>
        </div>
    );
};

interface ColSpans {
    source: number;
    operator: number;
    value: number;
    value2: number;
    modification: number;
    pattern: number;
    output: number;
}

interface InnerFieldsRendererProps {
    outerIndex: number;
    innerIndex: number;
    removeInner: (index: number) => void;
    sourceCategories: ParameterCategory[];
    valueCategories: ParameterCategory[];
    outputCategories: ParameterCategory[];
    showValue2Column: boolean;
    showPatternColumn: boolean;
    colSpans: ColSpans;
}

const InnerFieldsRenderer = ({
    outerIndex,
    innerIndex,
    removeInner,
    sourceCategories,
    valueCategories,
    outputCategories,
    showValue2Column,
    showPatternColumn,
    colSpans,
}: InnerFieldsRendererProps) => {
    const { control, getValues, setValue, trigger } = useGeneralForm<ModifyFieldFormData>();
    const { t } = usePaldockTranslation("components.connection-creator");

    // Watch the current operator to determine if value/value2 should be disabled
    const currentOperator = useWatch({
        control,
        name: `data.${outerIndex}.fields.${innerIndex}.operator`,
    });
    const isRegexOperator = REGEX_OPERATORS.includes(currentOperator as FilterType);
    const isValueDisabled = VALUE_DISABLED_OPERATORS.includes(currentOperator as FilterType);
    const isValue2Enabled = VALUE2_ENABLED_OPERATORS.includes(currentOperator as FilterType);
    const valueFieldName = `data.${outerIndex}.fields.${innerIndex}.value` as const;

    // Watch the current modification to determine if pattern should be disabled
    const currentModification = useWatch({
        control,
        name: `data.${outerIndex}.fields.${innerIndex}.modification`,
    });
    const isPatternEnabled = PATTERN_ENABLED_MODIFICATIONS.includes(currentModification as ModifyType);
    const isOutputDisabled = OUTPUT_DISABLED_MODIFICATIONS.includes(currentModification as ModifyType);
    const outputFieldName = `data.${outerIndex}.fields.${innerIndex}.output` as const;
    const patternFieldName = `data.${outerIndex}.fields.${innerIndex}.pattern` as const;

    useEffect(() => {
        if (!isOutputDisabled) return;
        if (!getValues(outputFieldName)) return;

        setValue(outputFieldName, "", {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
        });
    }, [getValues, isOutputDisabled, outputFieldName, setValue]);

    // The regex check on "value" only applies while a regex operator is selected, and the check on
    // "pattern" only while regex_replace is the modification — re-validate them when that changes,
    // otherwise a stale "invalid regex" error keeps showing after switching away. Skipped on mount so
    // a freshly added row doesn't immediately flash a "required" error before the user touches it.
    const isValueMount = useRef(true);
    useEffect(() => {
        if (isValueMount.current) {
            isValueMount.current = false;
            return;
        }
        trigger(valueFieldName);
    }, [currentOperator, trigger, valueFieldName]);

    const isPatternMount = useRef(true);
    useEffect(() => {
        if (isPatternMount.current) {
            isPatternMount.current = false;
            return;
        }
        trigger(patternFieldName);
    }, [currentModification, trigger, patternFieldName]);

    return (
        <div className="flex gap-2">
            <Row className="flex-1" gutter={8}>
                {/* Source - ParameterInput picker with fields, keys, and rows */}
                <Col span={colSpans.source}>
                    <FormItem control={control} name={`data.${outerIndex}.fields.${innerIndex}.source`}>
                        <TagTextInput
                            placeholder={t("nodes.modify.forms.source_placeholder")}
                            categories={sourceCategories}
                            mode={STEP_INPUT_POLICIES.modify.source.ui.mode}
                        />
                    </FormItem>
                </Col>
                {/* Operator - filter.type / condition_type */}
                <Col span={colSpans.operator}>
                    <FormItem control={control} name={`data.${outerIndex}.fields.${innerIndex}.operator`}>
                        <Select
                            placeholder={t("nodes.modify.forms.operator_placeholder")}
                            options={MODIFY_OPERATOR_OPTIONS.map((opt) => ({
                                value: opt.value,
                                label: t(opt.labelKey),
                            }))}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </FormItem>
                </Col>
                {/* Value - filter.params / condition_value (disabled for certain operators) */}
                <Col span={colSpans.value}>
                    <FormItem control={control} name={`data.${outerIndex}.fields.${innerIndex}.value`}>
                        {isRegexOperator ? (
                            <Input placeholder={t("nodes.modify.forms.value_placeholder")} disabled={isValueDisabled} />
                        ) : (
                            <TagTextInput
                                placeholder={t("nodes.modify.forms.value_placeholder")}
                                categories={valueCategories}
                                disabled={isValueDisabled}
                                mode={STEP_INPUT_POLICIES.modify.filterParams.ui.mode}
                            />
                        )}
                    </FormItem>
                </Col>
                {/* Value 2 - filter.params2 (enabled only for between/between_inclusive operators) */}
                {showValue2Column && (
                    <Col span={colSpans.value2}>
                        <FormItem control={control} name={`data.${outerIndex}.fields.${innerIndex}.value2`}>
                            <Input
                                placeholder={t("nodes.modify.forms.value2_placeholder")}
                                disabled={!isValue2Enabled}
                            />
                        </FormItem>
                    </Col>
                )}
                {/* Modification - change.type / change_type */}
                <Col span={colSpans.modification}>
                    <FormItem control={control} name={`data.${outerIndex}.fields.${innerIndex}.modification`}>
                        <Select
                            placeholder={t("nodes.modify.forms.modification_placeholder")}
                            options={MODIFY_MODIFICATION_OPTIONS.map((opt) => ({
                                value: opt.value,
                                label: t(opt.labelKey),
                            }))}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </FormItem>
                </Col>
                {/* Pattern - change.pattern (enabled only for regex_replace modification) */}
                {showPatternColumn && (
                    <Col span={colSpans.pattern}>
                        <FormItem control={control} name={`data.${outerIndex}.fields.${innerIndex}.pattern`}>
                            <Input
                                placeholder={t("nodes.modify.forms.pattern_placeholder")}
                                disabled={!isPatternEnabled}
                            />
                        </FormItem>
                    </Col>
                )}
                {/* Output - change.params / value_to_change */}
                <Col span={colSpans.output}>
                    <FormItem control={control} name={outputFieldName}>
                        <TagTextInput
                            placeholder={t("nodes.modify.forms.output_placeholder")}
                            categories={outputCategories}
                            disabled={isOutputDisabled}
                            mode={STEP_INPUT_POLICIES.modify.changeParams.ui.mode}
                        />
                    </FormItem>
                </Col>
            </Row>
            <Button
                className="h-[32px]"
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeInner(innerIndex)}
            />
        </div>
    );
};
